import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { Navigate } from "react-router-dom";
import { ArrowRight, Heart, Sparkles, ShieldCheck } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1774814319627-6b105a4dbf03?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxpbmRpYW4lMjB3ZWRkaW5nJTIwY291cGxlJTIwc3Vuc2V0fGVufDB8fHx8MTc4MjYzMTQ1Nnww&ixlib=rb-4.1.0&q=85";
const COUPLE2 = "https://images.unsplash.com/photo-1619930296170-1a7867d8f61a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxpbmRpYW4lMjB3ZWRkaW5nJTIwY291cGxlJTIwc3Vuc2V0fGVufDB8fHx8MTc4MjYzMTQ1Nnww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { user } = useAuth();
  const { t } = useLang();
  if (user) return <Navigate to={user.role === "admin" ? "/admin/users" : "/browse"} replace />;

  return (
    <div data-testid="landing-page">
      {/* Hero */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-24 md:pt-24 md:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 rise">
            <p className="overline mb-6" data-testid="hero-overline">{t("landing.overline")}</p>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight">
              {t("landing.title1")}
              <br />
              {t("landing.title2")}<br />
              <em className="italic text-[#C85A44]">{t("landing.title3")}</em>.
            </h1>
            <p className="mt-8 text-base md:text-lg max-w-xl text-[#5d574f] leading-relaxed">
              {t("landing.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/register"
                data-testid="hero-cta-register"
                className="group inline-flex items-center gap-2 rounded-full bg-[#C85A44] hover:bg-[#A84A36] text-white px-7 py-3.5 text-sm font-medium transition-all"
              >
                {t("landing.ctaCreate")}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5}/>
              </Link>
              <Link
                to="/login"
                data-testid="hero-cta-login"
                className="rounded-full border border-[#E2DCD0] hover:bg-[#F2EFE9] px-7 py-3.5 text-sm font-medium transition"
              >
                {t("landing.ctaHave")}
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6 relative">
            <div className="relative aspect-[4/5] rounded-3xl overflow-hidden">
              <img src={HERO} alt="Couple" className="absolute inset-0 w-full h-full object-cover"/>
            </div>
            <div className="hidden md:block absolute -bottom-12 -left-12 w-56 h-72 rounded-2xl overflow-hidden border-8 border-[#F9F8F6] shadow-xl">
              <img src={COUPLE2} alt="Couple" className="w-full h-full object-cover"/>
            </div>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 border-t border-[#E2DCD0]">
        <div className="grid md:grid-cols-3 gap-12 md:gap-20">
          <Pillar icon={<Heart strokeWidth={1.25} className="h-6 w-6 text-[#C85A44]" />}
            kicker={t("landing.pillar1.kicker")}
            title={t("landing.pillar1.title")}
            body={t("landing.pillar1.body")}
          />
          <Pillar icon={<ShieldCheck strokeWidth={1.25} className="h-6 w-6 text-[#C85A44]" />}
            kicker={t("landing.pillar2.kicker")}
            title={t("landing.pillar2.title")}
            body={t("landing.pillar2.body")}
          />
          <Pillar icon={<Sparkles strokeWidth={1.25} className="h-6 w-6 text-[#C85A44]" />}
            kicker={t("landing.pillar3.kicker")}
            title={t("landing.pillar3.title")}
            body={t("landing.pillar3.body")}
          />
        </div>
      </section>

      {/* CTA bottom */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-28">
        <div className="rounded-3xl bg-[#F2EFE9] p-10 md:p-20 text-center">
          <h2 className="font-serif-display text-4xl md:text-5xl tracking-tight">{t("landing.bottom.title")}</h2>
          <p className="mt-5 text-[#5d574f] max-w-2xl mx-auto">{t("landing.bottom.body")}</p>
          <Link
            to="/register"
            data-testid="footer-cta-register"
            className="inline-flex items-center gap-2 mt-8 rounded-full bg-[#C85A44] hover:bg-[#A84A36] text-white px-7 py-3.5 text-sm font-medium transition"
          >
            {t("landing.bottom.cta")} <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}

function Pillar({ icon, kicker, title, body }) {
  return (
    <div className="rise">
      <div className="mb-5">{icon}</div>
      <div className="overline mb-3">{kicker}</div>
      <h3 className="font-serif-display text-2xl mb-3 leading-tight">{title}</h3>
      <p className="text-sm text-[#5d574f] leading-relaxed">{body}</p>
    </div>
  );
}
